import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/app/Layout";
import MasonryGrid from "@/components/app/MasonryGrid";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/lib/categories";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "recent";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects", {
        params: { q: q || undefined, category: category !== "all" ? category : undefined, sort, limit: 48 },
      });
      setProjects(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, sort]);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="meta-label mb-2">Discover work from the community</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">Explore</h1>

        <div className="mt-8 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <form className="relative w-full lg:max-w-md" onSubmit={(e) => { e.preventDefault(); load(); }}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects, tags, creators…"
              className="pl-9"
              data-testid="explore-search-input"
            />
          </form>
          <div className="flex gap-2 flex-wrap">
            <Select value={category} onValueChange={(v) => setParams({ ...Object.fromEntries(params), category: v })}>
              <SelectTrigger className="w-[180px]" data-testid="explore-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => setParams({ ...Object.fromEntries(params), sort: v })}>
              <SelectTrigger className="w-[160px]" data-testid="explore-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Newest</SelectItem>
                <SelectItem value="trending">Trending</SelectItem>
                <SelectItem value="likes">Most liked</SelectItem>
                <SelectItem value="views">Most viewed</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} data-testid="explore-apply-btn">Apply</Button>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading…</div>
          ) : (
            <MasonryGrid projects={projects} empty="No work matches your filters yet." />
          )}
        </div>
      </section>
    </Layout>
  );
}
